import {
  HeadContent,
  Link,
  Outlet,
  Scripts,
  createRootRoute,
} from "@tanstack/react-router";
import { TracewayProvider } from "@tracewayapp/react";
import {
  jsonLd,
  organizationJsonLd,
  seoConfig,
  websiteJsonLd,
} from "~/config/seo";
import { Shell } from "~/components/site-shell";
import { GoogleAnalytics } from "~/components/google-analytics";
import { googleAnalyticsId } from "~/config/analytics";
import stylesheet from "~/styles.css?url";

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#f7f5ef" },
      ...(seoConfig.googleVerification
        ? [
            {
              name: "google-site-verification",
              content: seoConfig.googleVerification,
            },
          ]
        : []),
      ...(seoConfig.bingVerification
        ? [{ name: "msvalidate.01", content: seoConfig.bingVerification }]
        : []),
    ],
    links: [
      { rel: "stylesheet", href: stylesheet },
      { rel: "icon", href: "/favicon.ico", type: "image/x-icon" },
      { rel: "apple-touch-icon", href: "/images/logo.svg" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
    scripts: [jsonLd(organizationJsonLd()), jsonLd(websiteJsonLd())],
  }),
  notFoundComponent: NotFound,
  component: Root,
});

function NotFound() {
  return (
    <Shell>
      <section className="paper-grid">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-12">
          <p className="eyebrow text-moss">404 · Page not found</p>
          <h1 className="display mt-4 max-w-3xl text-6xl font-bold leading-[.9]">
            This page seems to have moved out.
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-8 text-ink/60">
            The address may be outdated, or the page may no longer be part of
            HostRight. Let&apos;s get you somewhere useful.
          </p>
          <Link
            to="/"
            className="button-primary mt-9 inline-flex rounded-full px-5 py-3 text-sm font-bold"
          >
            Back to HostRight
          </Link>
        </div>
      </section>
    </Shell>
  );
}

function Root() {
  const tracewayConnection = import.meta.env.VITE_TRACEWAY_CONNECTION;

  return (
    <html lang="en">
      <head>
        {googleAnalyticsId ? (
          <>
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleAnalyticsId)}`}
            />
            <script
              dangerouslySetInnerHTML={{
                __html: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', ${JSON.stringify(googleAnalyticsId)});`,
              }}
            />
          </>
        ) : null}
        <HeadContent />
      </head>
      <body>
        {googleAnalyticsId ? <GoogleAnalytics /> : null}
        {tracewayConnection ? (
          <TracewayProvider
            connectionString={tracewayConnection}
            options={{
              recordAllSessions: true,
              version: import.meta.env.VITE_APP_VERSION ?? "hostright",
            }}
          >
            <Outlet />
          </TracewayProvider>
        ) : (
          <Outlet />
        )}
        <Scripts />
      </body>
    </html>
  );
}
